import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/AppLayout";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicMobileNav } from "@/components/PublicMobileNav";
import { Field } from "@/pages/Login";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      if (data.email_sent) toast.success("Reset code sent to your email");
      else toast.warning(data.message || "Couldn't send the email — tap Resend in a moment");
      setStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    if (otp.trim().length !== 6) return toast.error("Enter the 6-digit code");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: email.trim().toLowerCase(), otp: otp.trim(), new_password: pw });
      toast.success("Password updated — please sign in");
      nav("/login", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute top-4 left-4 z-20"><PublicMobileNav /></div>
      <div className="absolute top-4 right-4 z-20"><ThemeToggle /></div>
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] sm:w-[700px] max-w-[120vw] h-[300px] sm:h-[400px] bg-[#00F0FF]/15 blur-[100px] sm:blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-6 sm:mb-8"><Logo /></div>
        <div className="glass-strong rounded-2xl p-6 sm:p-8">
          {step === 1 ? (
            <>
              <div className="w-11 h-11 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-4">
                <MailCheck className="text-[#00F0FF]" size={20} />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Forgot password?</h2>
              <p className="text-[#94A3B8] text-sm mt-1">Enter your email and we'll send a reset code.</p>
              <form onSubmit={sendCode} className="mt-6 space-y-4">
                <Field label="Email" type="email" value={email} onChange={setEmail} testid="fp-email" />
                <button type="submit" disabled={busy} data-testid="fp-send-code"
                        className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
                  {busy ? "Sending…" : "Send Reset Code"}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-4">
                <KeyRound className="text-[#00F0FF]" size={20} />
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">Reset password</h2>
              <p className="text-[#94A3B8] text-sm mt-1">Enter the code sent to <span className="text-white">{email}</span> and choose a new password.</p>
              <form onSubmit={reset} className="mt-6 space-y-4">
                <Field label="6-digit code" value={otp} onChange={(v) => setOtp(v.replace(/\D/g, "").slice(0, 6))} testid="fp-otp" />
                <Field label="New Password" type="password" value={pw} onChange={setPw} testid="fp-password" />
                <Field label="Confirm Password" type="password" value={confirm} onChange={setConfirm} testid="fp-confirm" />
                <button type="submit" disabled={busy} data-testid="fp-reset"
                        className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
                  {busy ? "Updating…" : "Reset Password"}
                </button>
                <button type="button" onClick={sendCode} disabled={busy} data-testid="fp-resend"
                        className="w-full text-sm text-[#00F0FF] hover:underline">Resend code</button>
              </form>
            </>
          )}
          <div className="text-sm text-[#94A3B8] text-center mt-6">
            <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="fp-back-login">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
